import React from 'react';
import { connect } from 'react-redux';
import { LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import sortBy from 'lodash/sortBy';
import { PropTypes } from 'prop-types';
import useDeepCompareEffect from 'use-deep-compare-effect';

import DeindexationStatusEnum from 'redpoints-model-js/enumeration/DeindexationStatusEnum';
import EnforcementStatusEnum from 'redpoints-model-js/enumeration/EnforcementStatusEnum';
import Incident from 'redpoints-model-js/model/incident/Incident';

import { Table } from 'redpoints-front-components/index';

import { allIncidentsTableFetchDeindexationByLink } from '../../../actions/allIncidentsActions';
import EnumController from '../../../enumerations/EnumController';
import { translate } from '../../../helpers/languageHelper';
import { childColumns } from './tableConfig';

const DEFAULT_DEINDEXATION = 0;

const SORT_LINK_TYPES_CONFIG = [
  {
    order: 1,
    linkTypeName: 'detectionLink',
    linkTypeIdKeyName: 'detectionLinkId',
  },
  {
    order: 2,
    linkTypeName: 'targetDetectionLink',
    linkTypeIdKeyName: 'detectionLinkId',
  },
  {
    order: 3,
    linkTypeName: 'contentLink',
    linkTypeIdKeyName: 'contentLinkId',
  },
  {
    order: 4,
    linkTypeName: 'shortenerLink',
    linkTypeIdKeyName: 'shortenerLinkId',
  },
];

export const LinkStatus = ({ enforcementStatusName, reasonName }) => {
  const showReason =
    reasonName &&
    enforcementStatusName === EnforcementStatusEnum.NO_ENFORCEMENT.name;

  return (
    <div className="link-status">
      {translate(
        EnumController.getLiteralKey(
          EnforcementStatusEnum,
          enforcementStatusName
        )
      )}
      {showReason && (
        <span>
          <WarningOutlined />
          {reasonName}
        </span>
      )}
    </div>
  );
};

const getAllLinksInformation = ({ assetId, incident }) =>
  sortBy(SORT_LINK_TYPES_CONFIG, 'order').reduce(
    (newLinksData, { linkTypeName, linkTypeIdKeyName }) => {
      const linkData = incident?.[linkTypeName];
      const linkId = linkData?.[linkTypeIdKeyName];

      if (linkId) {
        return [
          ...newLinksData,
          {
            ...linkData,
            assetId,
            incidentType: linkTypeName,
            linkId,
          },
        ];
      }

      return newLinksData;
    },
    []
  );

const renderDeindexationStatus = status =>
  translate(`allIncidents.linksTableHeader.deindexation.status.${status}`);

const getDeindexationStatus = deindexation =>
  deindexation[DEFAULT_DEINDEXATION].deindexationStatus.name;

const AllIncidentsTableItem = ({
  allIncidentsTableFetchDeindexationByLink,
  data,
  reportType,
}) => {
  const linksData = getAllLinksInformation(data);

  useDeepCompareEffect(() => {
    linksData.forEach(link => {
      const { deindexation, isLoading } = link;
      const needGetDeIndexation = !isLoading && !deindexation;
      if (needGetDeIndexation) {
        allIncidentsTableFetchDeindexationByLink({
          assetId: link.assetId,
          incidentId: data.incidentId,
          linkId: link.linkId,
          incidentType: link.incidentType,
        });
      }
    });
  }, [allIncidentsTableFetchDeindexationByLink, linksData]);

  const renderRowLink = url => (
    <span className="url-content-link">
      <a target="_blank" rel="noopener noreferrer" href={url}>
        {url}
      </a>
    </span>
  );

  const renderRowDeIndexation = (deindexation, record) => {
    if (!deindexation) {
      return <LoadingOutlined />;
    }

    if (!deindexation.length) {
      const isDomainDeindexable = record._domain?.deindexable;
      return isDomainDeindexable
        ? renderDeindexationStatus('notRequested')
        : renderDeindexationStatus('notDeindexable');
    }

    if (deindexation.length) {
      const status = getDeindexationStatus(deindexation);
      return renderDeindexationStatus(status);
    }
  };

  const renderRowRejectedReason = deindexation => {
    if (!deindexation) {
      return <LoadingOutlined />;
    }
    if (deindexation && deindexation.length) {
      const isStatusRejected =
        getDeindexationStatus(deindexation) ===
        DeindexationStatusEnum.REJECTED.name;

      const rejectedReason = isStatusRejected
        ? deindexation[DEFAULT_DEINDEXATION].deindexationRejectionReason?.name
        : null;
      return rejectedReason;
    }
    return null;
  };

  return (
    <Table
      dataSource={linksData}
      columns={childColumns({
        renderRowDeIndexation,
        renderRowLink,
        renderRowRejectedReason,
        reportType,
      })}
      rowKey={({ linkId }) => linkId}
      pagination={{ hideOnSinglePage: true }}
    />
  );
};

AllIncidentsTableItem.propTypes = {
  data: PropTypes.shape({
    assetId: PropTypes.number.isRequired,
    incidentId: PropTypes.number.isRequired,
    incident: PropTypes.instanceOf(Incident).isRequired,
  }),
};

export default connect(null, {
  allIncidentsTableFetchDeindexationByLink,
})(AllIncidentsTableItem);
